import type { WindowResponse } from "../schedule/scheduleApi";
import { endNotAfterStart, findOverlaps, type WindowDraft } from "../schedule/scheduleRules";

/**
 * The onboarding quick-hours model — a lunch range and a dinner range per day, mapped to and from the
 * whole-week `ServiceWindow` list the schedule PUT replaces wholesale. Everything is pure so the one
 * promise this step makes is unit-testable: re-saving never changes what it can represent, and a week it
 * can't represent comes back `advanced` (read-only in the wizard) instead of being flattened.
 */

export const DEFAULT_LUNCH = { start: "12:00", end: "14:00" } as const;
export const DEFAULT_DINNER = { start: "19:00", end: "22:00" } as const;

/** A single window starting at or after this time is a dinner service. */
const DINNER_FROM = "16:00";

export interface QuickRange {
  enabled: boolean;
  start: string; // "HH:mm"
  end: string; // "HH:mm"
  /** The service an existing window belongs to; null for a range the owner adds in this form. */
  serviceId: string | null;
}

export interface QuickDay {
  open: boolean;
  lunch: QuickRange;
  dinner: QuickRange;
}

export type QuickWeek = { kind: "simple"; days: QuickDay[] } | { kind: "advanced" };

const hhmm = (time: string) => time.slice(0, 5);

const range = (w: WindowResponse): QuickRange => ({
  enabled: true,
  start: hhmm(w.startTime),
  end: hhmm(w.endTime),
  serviceId: w.serviceId,
});

const defaultRange = (times: { start: string; end: string }, enabled: boolean): QuickRange => ({
  enabled,
  start: times.start,
  end: times.end,
  serviceId: null,
});

function toQuickDay(windows: WindowResponse[], freshWeek: boolean): QuickDay {
  const [first, second] = windows.toSorted((a, b) => a.startTime.localeCompare(b.startTime));
  if (!first) {
    return {
      open: freshWeek,
      lunch: defaultRange(DEFAULT_LUNCH, true),
      dinner: defaultRange(DEFAULT_DINNER, true),
    };
  }
  if (second) return { open: true, lunch: range(first), dinner: range(second) };
  // Only the dinner slot may cross midnight, so a late single range goes there whatever its start.
  const isDinner = first.crossesMidnight || hhmm(first.startTime) >= DINNER_FROM;
  return isDinner
    ? { open: true, lunch: defaultRange(DEFAULT_LUNCH, false), dinner: range(first) }
    : { open: true, lunch: range(first), dinner: defaultRange(DEFAULT_DINNER, false) };
}

function isRepresentable(windows: WindowResponse[]): boolean {
  const drafts = windows.map((w) => ({
    dayOfWeek: w.dayOfWeek,
    startTime: hhmm(w.startTime),
    endTime: hhmm(w.endTime),
    crossesMidnight: w.crossesMidnight,
    serviceId: "any",
  }));
  // The flag must match the wall clock, or re-deriving it on save would change the window.
  if (drafts.some((d) => d.crossesMidnight !== endNotAfterStart(d.startTime, d.endTime))) {
    return false;
  }
  for (let dow = 1; dow <= 7; dow++) {
    const day = drafts
      .filter((d) => d.dayOfWeek === dow)
      .toSorted((a, b) => a.startTime.localeCompare(b.startTime));
    if (day.length > 2) return false;
    if (day.length === 2 && day[0]?.crossesMidnight) return false;
  }
  // Compared as one service: different services may legally overlap, but one range per slot can't show it.
  return findOverlaps(drafts).size === 0;
}

/** Read the week into the form, or `advanced` when the form would have to change it to show it. */
export function toQuickWeek(windows: WindowResponse[]): QuickWeek {
  if (!isRepresentable(windows)) return { kind: "advanced" };
  const freshWeek = windows.length === 0;
  const days = Array.from({ length: 7 }, (_, i) =>
    toQuickDay(
      windows.filter((w) => w.dayOfWeek === i + 1),
      freshWeek,
    ),
  );
  return { kind: "simple", days };
}

const activeRanges = (day: QuickDay): QuickRange[] =>
  day.open ? [day.lunch, day.dinner].filter((r) => r.enabled) : [];

/** True when saving needs the default service (a range added here has no service yet). */
export function needsDefaultService(days: QuickDay[]): boolean {
  return days.some((day) => activeRanges(day).some((r) => r.serviceId === null));
}

/** Build the whole-week window list. Existing ranges keep their service; new ones use the default. */
export function toWindows(days: QuickDay[], defaultServiceId: string): WindowDraft[] {
  return days.flatMap((day, i) =>
    activeRanges(day).map((r) => ({
      dayOfWeek: i + 1,
      startTime: r.start,
      endTime: r.end,
      crossesMidnight: endNotAfterStart(r.start, r.end),
      serviceId: r.serviceId ?? defaultServiceId,
    })),
  );
}

export type QuickHoursIssue = "NO_RANGE" | "RANGE" | "OVERLAP";

/** Per-day problems, keyed by day index. A range-shape problem wins over an overlap on the same day. */
export function validateQuickDays(days: QuickDay[]): Map<number, QuickHoursIssue> {
  const issues = new Map<number, QuickHoursIssue>();
  days.forEach((day, i) => {
    if (!day.open) return;
    const { lunch, dinner } = day;
    // Saving this would quietly close a day the form shows as open.
    if (!lunch.enabled && !dinner.enabled) {
      issues.set(i, "NO_RANGE");
      return;
    }
    const lunchBad = lunch.enabled && (!isTime(lunch) || endNotAfterStart(lunch.start, lunch.end));
    const dinnerBad = dinner.enabled && (!isTime(dinner) || dinner.start === dinner.end);
    if (lunchBad || dinnerBad) issues.set(i, "RANGE");
  });

  const checkable = days.flatMap((day, i) =>
    activeRanges(day)
      .filter(() => !issues.has(i))
      .map((r) => ({
        dayIndex: i,
        window: {
          dayOfWeek: i + 1,
          startTime: r.start,
          endTime: r.end,
          crossesMidnight: endNotAfterStart(r.start, r.end),
          serviceId: "any",
        },
      })),
  );
  for (const index of findOverlaps(checkable.map((c) => c.window))) {
    const dayIndex = checkable[index]?.dayIndex;
    if (dayIndex !== undefined) issues.set(dayIndex, "OVERLAP");
  }
  return issues;
}

function isTime(r: QuickRange): boolean {
  return r.start !== "" && r.end !== "";
}

const copyTimes = (from: QuickRange, to: QuickRange): QuickRange => ({
  ...to,
  enabled: from.enabled,
  start: from.start,
  end: from.end,
});

/** Copy the first open day's times to every open day. Each range keeps its own service. */
export function applyFirstOpenDay(days: QuickDay[]): QuickDay[] {
  const source = days.find((day) => day.open);
  if (!source) return days;
  return days.map((day) =>
    day.open
      ? {
          ...day,
          lunch: copyTimes(source.lunch, day.lunch),
          dinner: copyTimes(source.dinner, day.dinner),
        }
      : day,
  );
}
